import { Box, Typography } from "@mui/material";
import { Dropdown } from "components";
import { Control } from "react-hook-form";
import {
	ContainmentTypeConfig,
	ShouldShowPartNumbersSummary,
} from "../hooks/containmentTypeConfig";
import * as labels from "./constants";
import { CardShell, ReadonlyField } from "./shared";
import { SinglePartFields } from "./SinglePartFields";

interface partNumberChoice {
	value: boolean;
	label: string;
}

const partNumberOptions: partNumberChoice[] = [
	{ value: false, label: labels.SinglePartLabel },
	{ value: true, label: labels.MultiplePartsLabel },
];

interface partNumbersCardProps {
	readonly?: boolean;
	/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
	control?: Control<any>;
	typeConfig?: ContainmentTypeConfig;
	hasMultipleParts?: boolean;
	partNumber?: string;
	supplierDunsName?: string | null;
	dunsFilterType?: any;
	showDuns?: boolean;
	uploadContentTypeLabel?: string | null;
	onPartNumberTypeChange?: (val: partNumberChoice | null) => void;
	/**
	 * Controls whether Part Number is required/validated when the card is shown.
	 * (Visibility of the card itself is controlled by `visible`.)
	 */
	partNumberRequired?: boolean;
	/** Hide the entire card in create/edit when the FlexNet version gate does not require Part Number. */
	visible?: boolean;
}

export function PartNumbersCard({
	readonly = false,
	control,
	typeConfig,
	hasMultipleParts = false,
	partNumber,
	supplierDunsName,
	dunsFilterType,
	showDuns = false,
	uploadContentTypeLabel,
	onPartNumberTypeChange,
	partNumberRequired = true,
	visible = true,
}: partNumbersCardProps): React.JSX.Element {
	if (
		readonly &&
		!ShouldShowPartNumbersSummary({
			typeConfig,
			hasMultipleParts,
			partNumber,
			supplierDunsName,
			uploadContentTypeLabel,
		})
	) {
		return <></>;
	}

	if (readonly && typeConfig?.showUploadContentType) {
		return (
			<CardShell readonly label={labels.UploadContentType}>
				<ReadonlyField label={labels.Type} value={uploadContentTypeLabel ?? ""} />
			</CardShell>
		);
	}

	if (!readonly && !visible) {
		return <></>;
	}

	return (
		<CardShell readonly={readonly} label={labels.PartNumbersImpacted} toolTip="Select one or multiple parts">
			{readonly ? (
				<>
					{(hasMultipleParts || partNumber) && (
						<ReadonlyField
							label={labels.Parts}
							value={hasMultipleParts ? labels.MultiplePartsReadonly : labels.SinglePartReadonly}
						/>
					)}
					{!hasMultipleParts && partNumber && (
						<ReadonlyField label={labels.PartNumber} value={partNumber} />
					)}
					{!hasMultipleParts && supplierDunsName && (
						<ReadonlyField label={labels.SupplierDuns} value={supplierDunsName} />
					)}
				</>
			) : (
				<>
					<Box data-test-selector="part-numbers-impacted-dropdown">
						<Typography sx={{ fontWeight: 600, fontSize: "0.8rem", mb: 0.5 }}>
							{labels.PartNumbersImpacted}
						</Typography>
						<Dropdown
							data={partNumberOptions}
							value={
								partNumberOptions.find((o) => o.value === hasMultipleParts) ??
								partNumberOptions[0]
							}
							getOptionLabel={(o) => o.label}
							isOptionEqualToValue={(a, b) => a.value === b.value}
							onChange={onPartNumberTypeChange}
							fullWidth
							multiple={false}
							label={labels.PartNumbersImpacted}
							hideLabel
							clearable={false}
						/>
					</Box>

					<SinglePartFields
						control={control!}
						hasMultipleParts={hasMultipleParts}
						dunsFilterType={dunsFilterType}
						showDuns={showDuns}
						partNumberRequired={partNumberRequired}
					/>
				</>
			)}
		</CardShell>
	);
}
